import type {
  BodyPartKey,
  TrackerKey,
} from "../../../../../types/clientConfig";

/** What `GET client-configurations/push-dashboard-defaults` returns under `defaults` (sparse). */
export interface PushDashboardDefaults {
  quickLog?: boolean;
  floatingLog?: boolean;
  /** `null` / absent = follow Getting Started, `false` = off. */
  dailyGoals?: boolean | null;
  metrics?: Partial<Record<TrackerKey, boolean>>;
  bodyParts?: BodyPartKey[];
  /** Progress Overview focus (an insights lens slug) until the subscriber picks their own. */
  trainingFocus?: string;
}

export interface PushFeatureStatus {
  training: boolean;
  nutrition: boolean;
  mealPlans: boolean;
  dailyAccountabilities: boolean;
}

/** What a subscriber starts with when the coach saved nothing; served by the API. */
export interface PushMsbDefaults {
  quickLog: boolean;
  floatingLog: boolean;
  metrics: Record<TrackerKey, boolean>;
  bodyParts: BodyPartKey[];
}

export interface PushDashboardLoaded {
  defaults: PushDashboardDefaults;
  featureStatus: PushFeatureStatus;
  msbDefaults: PushMsbDefaults;
}

/** The whole screen, every value explicit. */
export interface PushDashboardFormState {
  quickLog: boolean;
  floatingLog: boolean;
  /** On = follow Getting Started (sent as nothing), off = `false`. */
  dailyGoals: boolean;
  metrics: Record<TrackerKey, boolean>;
  bodyParts: BodyPartKey[];
  /** "" = no default */
  trainingFocus: string;
}

/** Metric tiles in the order of the design. */
export const PUSH_METRIC_ORDER: TrackerKey[] = [
  "readiness",
  "sessionRpe",
  "bodyWeight",
  "progressPhotos",
  "steps",
  "sleep",
  "hrv",
  "restingHeartRate",
  "water",
  "mealPhotos",
  "bodyFat",
  "bodyMeasurements",
];

/** Trackers that follow each subscriber's training days instead of Everyday. */
export const TRAINING_DAY_METRICS: TrackerKey[] = ["readiness", "sessionRpe"];

const sameSet = <T>(a: T[], b: T[]): boolean =>
  a.length === b.length && a.every((item) => b.includes(item));

/** The form for the saved defaults, filling every setting the coach did not set with the MSB default. */
export const buildFormState = (
  defaults: PushDashboardDefaults,
  msbDefaults: PushMsbDefaults
): PushDashboardFormState => ({
  quickLog: defaults.quickLog ?? msbDefaults.quickLog,
  floatingLog: defaults.floatingLog ?? msbDefaults.floatingLog,
  dailyGoals: defaults.dailyGoals !== false,
  metrics: Object.fromEntries(
    PUSH_METRIC_ORDER.map((key) => [
      key,
      defaults.metrics?.[key] ?? msbDefaults.metrics[key],
    ])
  ) as Record<TrackerKey, boolean>,
  bodyParts: defaults.bodyParts?.length
    ? defaults.bodyParts
    : msbDefaults.bodyParts,
  trainingFocus: defaults.trainingFocus ?? "",
});

export const countActiveMetrics = (state: PushDashboardFormState): number =>
  PUSH_METRIC_ORDER.filter((key) => state.metrics[key]).length;

/** Quick Log and the floating button need at least one metric on, the API forces them off otherwise. */
export const hasActiveMetric = (state: PushDashboardFormState): boolean =>
  countActiveMetrics(state) > 0;

export const isSameFormState = (
  a: PushDashboardFormState,
  b: PushDashboardFormState
): boolean =>
  a.quickLog === b.quickLog &&
  a.floatingLog === b.floatingLog &&
  a.dailyGoals === b.dailyGoals &&
  PUSH_METRIC_ORDER.every((key) => a.metrics[key] === b.metrics[key]) &&
  sameSet(a.bodyParts, b.bodyParts) &&
  a.trainingFocus === b.trainingFocus;

/**
 * The object to `PUT`: only what differs from the MSB defaults, so a coach who changes nothing (or
 * resets) stores nothing. The API replaces the saved defaults with it.
 */
export const buildPayload = (
  state: PushDashboardFormState,
  msbDefaults: PushMsbDefaults
): PushDashboardDefaults => {
  const payload: PushDashboardDefaults = {};
  const canLog = hasActiveMetric(state);

  const quickLog = state.quickLog && canLog;
  if (quickLog !== msbDefaults.quickLog) payload.quickLog = quickLog;

  const floatingLog = state.floatingLog && canLog;
  if (floatingLog !== msbDefaults.floatingLog) {
    payload.floatingLog = floatingLog;
  }

  if (!state.dailyGoals) payload.dailyGoals = false;

  const metrics: Partial<Record<TrackerKey, boolean>> = {};
  for (const key of PUSH_METRIC_ORDER) {
    if (state.metrics[key] !== msbDefaults.metrics[key]) {
      metrics[key] = state.metrics[key];
    }
  }
  if (Object.keys(metrics).length > 0) payload.metrics = metrics;

  if (!sameSet(state.bodyParts, msbDefaults.bodyParts)) {
    payload.bodyParts = state.bodyParts;
  }

  if (state.trainingFocus) payload.trainingFocus = state.trainingFocus;

  return payload;
};

/** Toggles a body part keeping the canonical order; the last selected one cannot be removed. */
export const toggleBodyPart = (
  selected: BodyPartKey[],
  part: BodyPartKey,
  allParts: BodyPartKey[]
): BodyPartKey[] => {
  const next = selected.includes(part)
    ? selected.filter((item) => item !== part)
    : [...selected, part];
  if (next.length === 0) return selected;
  return allParts.filter((item) => next.includes(item));
};
